import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Button, TextInput, Label, Select, Spinner } from 'flowbite-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import API from '../../utils/constant';
import {
  PublicExam,
  PublicGrade,
  PublicSubject,
  publicGetSchoolGrades,
  publicGetSchoolGradeSubjects,
  editExamInfo,
  adminTransferExam,
  AdminInstitutionBasic,
  adminListInstitutionsBasic,
  adminGetExams,
  AdminExamListItem,
} from '../../utils/remote';

const AdminExams: React.FC = () => {
  const { token, role } = useAuth();
  const superOnly = role === 'superuser';

  const [schools, setSchools] = useState<AdminInstitutionBasic[]>([]);
  const [grades, setGrades] = useState<PublicGrade[]>([]);
  const [subjects, setSubjects] = useState<PublicSubject[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<number | ''>('');
  const [selectedGrade, setSelectedGrade] = useState<number | ''>('');
  const [selectedSubject, setSelectedSubject] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc'>('name_asc');

  const [loading, setLoading] = useState(false);
  const [exams, setExams] = useState<(PublicExam | AdminExamListItem)[]>([]);

  const [editing, setEditing] = useState<PublicExam | null>(null);
  const [editName, setEditName] = useState('');
  const [editTime, setEditTime] = useState<string>('');

  // Transfer modal state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferInstitutionId, setTransferInstitutionId] = useState<number | ''>('');
  const [replicateChapters, setReplicateChapters] = useState(true);
  const [replicateQuestions, setReplicateQuestions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const insts = await adminListInstitutionsBasic();
        if (!cancelled) setSchools(insts);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedSchool) {
      setGrades([]);
      setSelectedGrade('');
      return;
    }
    (async () => {
      try {
        const gr = await publicGetSchoolGrades(selectedSchool as number);
        if (!cancelled) setGrades(gr);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [selectedSchool]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedSchool || !selectedGrade) {
      setSubjects([]);
      setSelectedSubject('');
      return;
    }
    (async () => {
      try {
        const subs = await publicGetSchoolGradeSubjects(selectedSchool as number, selectedGrade as number);
        if (!cancelled) setSubjects(subs);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [selectedSchool, selectedGrade]);

  const sortedExams = useMemo(() => {
    const arr = [...exams];
    arr.sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (sortBy === 'name_asc') return an.localeCompare(bn);
      return bn.localeCompare(an);
    });
    return arr;
  }, [exams, sortBy]);

  async function loadExams() {
    setLoading(true);
    try {
      // Use admin endpoint with filters. It supports institution, grade, subject, search and sort.
      const res = await adminGetExams({
        institution: selectedSchool || null,
        grade: selectedGrade || null,
        subject: selectedSubject || null,
        search: search || undefined,
        sort: sortBy === 'name_asc' ? 'name' : '-name',
        page: 1,
        page_size: 100,
      });
      setExams(res.results || []);
    } catch (e) {
      setExams([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // initial load
    loadExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    loadExams();
  }

  function onRowClick(ex: PublicExam) {
    setEditing(ex);
    setEditName(ex.name || '');
    setEditTime((ex as any).time?.toString() || (ex.time_given != null ? String(ex.time_given) : ''));
  }

  async function onSaveBasic() {
    if (!editing) return;
    // Save supported fields via existing school edit endpoint
    try {
      setLoading(true);
      await editExamInfo({ toBeValidated: token, exam_id: String(editing.id), payload: { name: editName, time_minutes: editTime } as any });
      // Update local
      setExams((prev) => prev.map((e) => (e.id === editing.id ? { ...e, name: editName, time_given: Number(editTime) } : e)));
      setEditing(null);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  }

  async function onConfirmTransfer() {
    if (!editing || !transferInstitutionId) return;
    try {
      setLoading(true);
      await adminTransferExam(editing.id as number, {
        target_institution_id: transferInstitutionId as number,
        replicate_chapters: replicateChapters,
        replicate_questions: replicateQuestions,
      });
      setShowTransfer(false);
      setEditing(null);
      // Reload exams to show potential newly created one when filters match
      await loadExams();
    } catch (e) {
      // TODO: surface error via toast if available
    } finally {
      setLoading(false);
    }
  }

  if (!superOnly) {
    return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-black dark:text-white">Manage All Exams</h1>

      <form onSubmit={onSearchSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label htmlFor="school" value="School / Institute" />
          <Select id="school" value={selectedSchool as any} onChange={(e) => { setSelectedSchool(e.target.value ? Number(e.target.value) : ''); }}>
            <option value="">All</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
        <div>
          <Label htmlFor="grade" value="Grade" />
          <Select id="grade" value={selectedGrade as any} onChange={(e) => { setSelectedGrade(e.target.value ? Number(e.target.value) : ''); }} disabled={!selectedSchool}>
            <option value="">All</option>
            {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        </div>
        <div>
          <Label htmlFor="subject" value="Subject" />
          <Select id="subject" value={selectedSubject as any} onChange={(e) => { setSelectedSubject(e.target.value ? Number(e.target.value) : ''); }} disabled={!selectedGrade}>
            <option value="">All</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
        <div>
          <Label htmlFor="search" value="Search by name" />
          <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Exam name..." />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="sort" value="Sort" />
            <Select id="sort" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
            </Select>
          </div>
          <Button type="submit" className="mt-6">{loading ? <Spinner size="sm" /> : 'Search'}</Button>
        </div>
      </form>

      <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="max-w-full overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Subject</Table.HeadCell>
              <Table.HeadCell>Grade</Table.HeadCell>
              <Table.HeadCell>Institute</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Time (min)</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {sortedExams.map((ex) => (
                <Table.Row key={ex.id} className="cursor-pointer" onClick={() => onRowClick(ex as any)}>
                  <Table.Cell className="font-medium text-slate-700 dark:text-white">{(ex as any).name}</Table.Cell>
                  <Table.Cell>{(ex as any).sub || (ex as any).subject_name || ''}</Table.Cell>
                  <Table.Cell>{(ex as any).grade || ''}</Table.Cell>
                  <Table.Cell>{(ex as any).institution_name || ''}</Table.Cell>
                  <Table.Cell>{(ex as any).status || ''}</Table.Cell>
                  <Table.Cell>{(ex as any).time ?? (ex as any).time_given ?? ''}</Table.Cell>
                </Table.Row>
              ))}
              {sortedExams.length === 0 && !loading && (
                <Table.Row>
                  <Table.Cell colSpan={5} className="text-center text-slate-500">No exams found</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>

      <Modal show={!!editing} size="4xl" onClose={() => setEditing(null)}>
        <Modal.Header>Edit Exam</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ename" value="Exam Name" />
                <TextInput id="ename" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="etime" value="Time (minutes)" />
                <TextInput id="etime" type="number" value={editTime} onChange={(e) => setEditTime(e.target.value)} />
              </div>
            </div>
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-800">
              Changing creator school, creator user, subject/course, exam scope, or national exam affects related tables and constraints:
              - Exam.institution is derived from subject/course; they must belong to the same institution.
              - Questions are linked via chapters/subchapters which belong to a subject/course hierarchy.
              - If you move an exam to a different subject or institution, related chapters/questions may need reassignment.
              Please confirm any cascading changes with the backend. We can update name/time now; for ownership and classification changes, let us know so we can add the corresponding backend endpoints.
            </div>
            {/* Placeholders for future fields (disabled now) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60 pointer-events-none">
              <div>
                <Label value="Creator School (institution)" />
                <Select disabled>
                  <option>Keep unchanged</option>
                </Select>
              </div>
              <div>
                <Label value="Creator User" />
                <Select disabled>
                  <option>Keep unchanged</option>
                </Select>
              </div>
              <div>
                <Label value="Field of Study / Subject" />
                <Select disabled>
                  <option>Keep unchanged</option>
                </Select>
              </div>
              <div>
                <Label value="Scope / National Exam" />
                <Select disabled>
                  <option>Keep unchanged</option>
                </Select>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="light" onClick={() => setShowTransfer(true)}>
            Transfer exam
          </Button>
          <Button onClick={onSaveBasic}>{loading ? <Spinner size="sm" /> : 'Save name/time'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTransfer} onClose={() => setShowTransfer(false)}>
        <Modal.Header>Transfer Exam to Another Institution</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label value="Target Institution" />
              <Select value={transferInstitutionId as any} onChange={(e) => setTransferInstitutionId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Select institution...</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={replicateChapters} onChange={(e) => setReplicateChapters(e.target.checked)} />
                <span>Replicate chapters/subchapters</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={replicateQuestions} onChange={(e) => setReplicateQuestions(e.target.checked)} />
                <span>Replicate questions</span>
              </label>
            </div>
            <div className="text-sm text-slate-600">
              This will create a copy of the exam and its structures under the selected institution. The new exam will be created as Draft.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setShowTransfer(false)}>Close</Button>
          <Button onClick={onConfirmTransfer}>{loading ? <Spinner size="sm" /> : 'Confirm transfer'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminExams;
